import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox, Collapse} from "@material-tailwind/react";
import Card from "../../../components/Card";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import FilterDropdown from "../../../components/FilterDropdown";
import {clear, validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {useParams} from "react-router-dom";
import {
    BrokerPaymentMethod, BrokerPaymentMethodBanks, BrokerPaymentMethodBanksResponse,
    BrokerPaymentMethodCashier,
    BrokerPaymentMethodCashiersResponse,
    BrokerPaymentMethodCountriesResponse,
    BrokerPaymentMethodCredentialsResponse,
    BrokerPaymentMethodInputs,
    BrokerPaymentMethodsResponse,
    BrokerProviderPaymentMethod
} from "../../../types/Responses/BrokerPaymentMethodsResponse";
import {User} from "../../../types/User";
import ProviderAvatar from "../../../components/ProviderAvatar";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import Loader from "../../../assets/icons/Loader";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import GenericInputs from "../../../components/GenericInputs";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import DropdownInput from "../../../components/DropdownInput";
import MaterialSelect from "../../../components/MaterialSelect";
import OutlineInput from "../../../components/OutlineInput";
import OutlineTextarea from "../../../components/OutlineTextarea";
import CountryAvatar from "../../../components/CountryAvatar";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import {AllRegionsResponse} from "../../../types/Responses/RegionsResponse";
import {useUserContext} from "../../../types/contexts/UserContext";
import DraggableTable from "./DraggableTable";
import RemovableCountry from "../../../components/RemovableCountry";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {setSidebarTitle} from "../../../reducers/sidebar";

const BrokerPaymentMethods = () => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const system = useSystemContext();
    const user = useUserContext();
    const {id} = useParams();
    const handleError = useErrorHandler();

    const [broker, setBroker] = useState<User>();
    const [paymentMethods, setPaymentMethods] = useState<BrokerProviderPaymentMethod[]>();
    const [allCountries, setAllCountries] = useState<CountriesShortResponse>();
    const [allRegions, setAllRegions] = useState<AllRegionsResponse>();
    const [allowedCountries, setAllowedCountries] = useState<number[]>();
    const [provider, setProvider] = useState<number>(0);
    const [openProvider, setOpenProvider] = useState<number>(0);
    const [selectedCurrency, setSelectedCurrency] = useState<number>(-1);
    const [inProgress, setInProgress] = useState<boolean>(false);
    const [isProvider, setIsProvider] = useState<boolean>(false);

    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [modalPaymentMethod, setModalPaymentMethod] = useState<BrokerPaymentMethod>();
    const [inputs, setInputs] = useState<BrokerPaymentMethodInputs>();
    const [banks, setBanks] = useState<BrokerPaymentMethodBanks[]>([]);
    const [values, setValues] = useState<object>();
    const [countries, setCountries] = useState<number[]>([]);
    const [regions, setRegions] = useState<number[]>([]);
    const [cashiers, setCashiers] = useState<BrokerPaymentMethodCashier[]>();
    const modals = {
        credentials: useComponentVisible(false),
        countries: useComponentVisible(false, '#countries_form'),
        cashier: useComponentVisible(false),
        banks: useComponentVisible(false),
    };
    const [sortingMethods, setSortingMethods] = useState<BrokerPaymentMethod[]>();
    const [isSortingMode, setIsSortingMode] = useState<boolean>(false);
    const dispatch = useDispatch();

    const transformResponse = useCallback((methods: BrokerPaymentMethodsResponse) => {
        const result = [] as BrokerProviderPaymentMethod[];
        setSortingMethods([...methods.data].sort((a, b) => a.order && b.order ? (a.order > b.order ? 1 : -1) : (a.order && !b.order ? 1 : -1)).filter(m => m.order));
        methods.data.forEach((item) => {
            let index = 0;
            const element = result.find((i, idx) => {
                index = idx;
                return i.provider.id === item.provider_id
            })
            if (!element) {
                result.push({
                    provider: {
                        id: item.provider_id,
                        name: item.provider_name,
                        image: item.image,
                        enabled_count: item.status ? 1 : 0
                    },
                    methods: [item]
                })
            } else if (result[index]) {
                // @ts-expect-error can be undefined
                result[index].methods.push(item);
                // @ts-expect-error can be undefined
                result[index].provider.enabled_count += item.status ? 1 : 0;
            }
        })

        return result;
    }, []);

    const sendRequest = useCallback(() => {
        API.getBrokerPaymentMethods(parseInt(id ?? '')).then((response: AxiosResponse) => {
            setPaymentMethods(transformResponse(response.data));
        }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKER_PAYMENT_METHODS.replace('$user', id?.toString() ?? '')}`, toast, handleError, setIsForbidden));
    }, [id, setPaymentMethods, toast, handleError]);

    const sendUserRequest = useCallback(() => {
        API.getUser(parseInt(id ?? '')).then((response: AxiosResponse) => {
            setBroker(response.data.data as User)
        }).catch(e => ErrorHandler(e, `get${APILink.GET_USER.replace('$user', id?.toString() ?? '')}`, toast, handleError, setIsForbidden));
    }, [id, setBroker, toast, handleError]);

    const onOpenProvider = useCallback((id: number) => {
        setOpenProvider(id);
    }, [setOpenProvider]);

    const onProviderChanged = useCallback((id: string | number) => {
        if (typeof id === 'string')
            id = parseInt(id);
        setProvider(id);
    }, [setProvider]);

    const openCredentialsModal = useCallback((method: BrokerPaymentMethod, isProviderModal = false) => {
        if (method.enabled_id) {
            setIsProvider(isProviderModal);
            setModalPaymentMethod(method);
            API.getBrokerPaymentMethodCredentials(method.enabled_id).then((response: AxiosResponse) => {
                const data = response.data.data as BrokerPaymentMethodCredentialsResponse;
                setInputs(data.inputs);
                // @ts-expect-error can be undefined
                setValues(data.values.multikeys ? {...data.values, ...data.values.multikeys} : data.values);
                modals.credentials.setIsComponentVisible(true);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKER_PAYMENT_METHOD_CREDENTIALS.replace('$brokerPaymentMethod', method.enabled_id?.toString() ?? '')}`, toast, handleError));
        }
    }, [setModalPaymentMethod, modals.credentials, toast, handleError]);

    const openCountriesModal = useCallback((method: BrokerPaymentMethod) => {
        if (!allCountries || !allRegions) {
            API.getCountriesShortList().then((response: AxiosResponse) => {
                setAllCountries(response.data as CountriesShortResponse);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));

            API.getRegionsShortList().then((response: AxiosResponse) => {
                setAllRegions(response.data as AllRegionsResponse);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_REGIONS_SHORT_LIST}`, toast, handleError));
        }
        if (method.enabled_id) {
            setModalPaymentMethod(method);
            API.getBrokerPaymentMethodCountries(method.enabled_id).then((response: AxiosResponse) => {
                const resp = response.data as BrokerPaymentMethodCountriesResponse;
                setCountries(resp.data)
                setAllowedCountries(resp.allowed);
                modals.countries.setIsComponentVisible(true);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKER_PAYMENT_METHOD_COUNTRIES.replace('$brokerPaymentMethod', method.enabled_id?.toString() ?? '')}`, toast, handleError));
        }
    }, [setModalPaymentMethod, setCountries, setAllCountries, setAllowedCountries, setAllRegions, modals.countries, allCountries, allRegions, toast, handleError]);

    const openCashiersModal = useCallback((method: BrokerPaymentMethod) => {
        if (method.enabled_id) {
            setModalPaymentMethod(method);
            API.getBrokerPaymentMethodCashier(method.enabled_id).then((response: AxiosResponse) => {
                const resp = response.data as BrokerPaymentMethodCashiersResponse;
                setCashiers(resp.data);
                modals.cashier.setIsComponentVisible(true);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKER_PAYMENT_METHOD_CASHIER.replace('$brokerPaymentMethod', method.enabled_id?.toString() ?? '')}`, toast, handleError));
        }
    }, [setModalPaymentMethod, setCountries, setAllCountries, setAllowedCountries, modals.cashier, toast, handleError]);

    const openBanksModal = useCallback((method: BrokerPaymentMethod) => {
        setBanks([]);
        if (method.enabled_id) {
            setModalPaymentMethod(method);
            API.getBrokerPaymentMethodBanks(method.enabled_id).then((response: AxiosResponse) => {
                const resp = response.data as BrokerPaymentMethodBanksResponse;
                setBanks(resp.data);
                modals.banks.setIsComponentVisible(true);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKER_PAYMENT_METHOD_BANKS.replace('$brokerPaymentMethod', method.enabled_id?.toString() ?? '')}`, toast, handleError));
        }
    }, [setModalPaymentMethod, setBanks, modals.banks, toast, handleError]);


    const onEnableChanged = useCallback((providerIndex: number, methodIndex: number, status: boolean) => {
        if (paymentMethods && broker) {
            const temp = provider ? paymentMethods.filter(p => p.provider.id === provider) : paymentMethods;
            const newPaymentMethods = [...temp];
            // @ts-expect-error can be undefined
            newPaymentMethods[providerIndex].methods[methodIndex].status = status;
            setPaymentMethods(newPaymentMethods);
            setInProgress(true);
            // @ts-expect-error can be undefined
            const method = newPaymentMethods[providerIndex].methods[methodIndex].id;
            API.switchBrokerPaymentMethodStatus(broker.id, method, status).then((response: AxiosResponse) => {
                if (response.data.data) {
                    // @ts-expect-error can be undefined
                    newPaymentMethods[providerIndex].methods[methodIndex] = {...newPaymentMethods[providerIndex].methods[methodIndex], ...response.data.data}
                    setPaymentMethods(newPaymentMethods);
                    let message = '';
                    if (status) {
                        // @ts-expect-error can be undefined
                        message = t('admin.broker_payment_methods.updated.was_enabled', {name: newPaymentMethods[providerIndex].methods[methodIndex].name});
                    } else {
                        // @ts-expect-error can be undefined
                        message = t('admin.broker_payment_methods.updated.was_disabled', {name: newPaymentMethods[providerIndex].methods[methodIndex].name})
                    }
                    toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                                   className="mr-4"/> {message}</>, "bg-teal-500");
                    setInProgress(false);
                }
            }).catch(e => ErrorHandler(e, `patch${APILink.SWITCH_BROKER_PAYMENT_METHOD_STATUS.replace('$user', broker.id.toString()).replace('$method', method.toString())}`, toast, handleError));
        }
    }, [paymentMethods, broker, provider, setPaymentMethods]);


    const credentialsCallback = useCallback((b: boolean) => {
        if (b && modalPaymentMethod && modalPaymentMethod.enabled_id && values) {
            API.updateBrokerPaymentMethodCredentials(modalPaymentMethod.enabled_id, {
                ...values,
                isProvider: isProvider ? 1 : 0
            }).then(() => {
                setInProgress(false);
                closeCredentialsModal();
                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/> {t('admin.broker_payment_methods.updated.credentials.title', {name: isProvider ? 'provider ' + modalPaymentMethod.provider_name : modalPaymentMethod.name})}l</>, "bg-teal-500");
            });
        } else setInProgress(false);
    }, [modalPaymentMethod, values, isProvider]);

    const saveCredentials = useCallback(() => {
        if (!values || !inputs) {
            toast?.show("Please introduce data", "bg-red-400");
            return;
        }
        setInProgress(true);
        dispatch(validateData({
            payload: {
                inputs: [],
                introducedData: values,
                callback: credentialsCallback
            }
        }));
    }, [values, inputs, toast]);

    const closeCredentialsModal = useCallback(() => {
        modals.credentials.setIsComponentVisible(false);
        setValues({});
        setInputs(undefined);
    }, [modals.credentials])

    const closeCountriesModal = useCallback(() => {
        modals.countries.setIsComponentVisible(false);
        setCountries([]);
    }, [modals.countries])

    const closeCashiersModal = useCallback(() => {
        modals.cashier.setIsComponentVisible(false);
        setCashiers([]);
    }, [modals.cashier]);

    const closeBanksModal = useCallback(() => {
        modals.banks.setIsComponentVisible(false);
        setBanks([]);
    }, [modals.cashier])

    const setLocalValue = useCallback((key: string, value: string | number) => {
        if (values) {
            const tempValues = {...values, [key as keyof typeof values]: value}
            setValues(tempValues);
        }
    }, [values]);

    const onBankStatusChanged = useCallback((id: number, status: boolean) => {
        if (banks.length) {
            const newBanks = [...banks];
            if (typeof newBanks[id] !== 'undefined' && newBanks[id]) {
                // @ts-expect-error can be undefined
                newBanks[id].enabled = status;
            }
            setBanks(newBanks);
        }
    }, [banks]);

    const saveCountries = useCallback(() => {
        if (modalPaymentMethod && modalPaymentMethod.enabled_id && countries) {
            API.updateBrokerPaymentMethodCountries(modalPaymentMethod.enabled_id, {countries}).then(() => {
                setInProgress(false);
                closeCountriesModal();
                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/> C{t('admin.broker_payment_methods.updated.countries.title', {name: modalPaymentMethod.name})}</>, "bg-teal-500");
            });
        } else setInProgress(false);
    }, [modalPaymentMethod, countries]);

    const saveCashiers = useCallback(() => {
        if (modalPaymentMethod && modalPaymentMethod.enabled_id && cashiers) {
            API.updateBrokerPaymentMethodCashier(modalPaymentMethod.enabled_id, {cashiers}).then(() => {
                setInProgress(false);
                closeCashiersModal();
                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/> {t('admin.broker_payment_methods.updated.cashiers.title', {name: modalPaymentMethod.name})}</>, "bg-teal-500");
            });
        } else setInProgress(false);
    }, [modalPaymentMethod, cashiers]);

    const saveBanks = useCallback(() => {
        if (modalPaymentMethod && modalPaymentMethod.enabled_id && banks.length) {
            API.updateBrokerPaymentMethodBanks(modalPaymentMethod.enabled_id, {banks}).then(() => {
                setInProgress(false);
                closeBanksModal();
                toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                               className="mr-4"/> {t('admin.broker_payment_methods.updated.banks.title', {name: modalPaymentMethod.name})}</>, "bg-teal-500");
            });
        } else setInProgress(false);
    }, [modalPaymentMethod, banks]);

    const changeCountries = useCallback((s: string | number) => {
        const newCountries = countries ? [...countries] : [];
        setCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCountries, countries]);

    const onCountryRemove = useCallback((index: number) => {
        if (countries) {
            const newCountries = [...countries];
            if (newCountries[index]) {
                newCountries.splice(index, 1)
            }
            setCountries(newCountries)
        }
    }, [countries, setCountries]);

    const changeRegions = useCallback((s: string | number) => {
        if (!allRegions || !allowedCountries)
            return;
        const newRegions = regions ? [...regions] : [];
        const filteredRegions = [...newRegions, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        });
        const regionCountries = allRegions.data.filter(i => filteredRegions.includes(i.id)).map(r => r.countries);
        let newCountries = [];
        for (let i = 0; i < regionCountries.length; i++) {
            // @ts-expect-error can be undefined
            newCountries = newCountries.concat(regionCountries[i]);
        }
        setRegions(filteredRegions);
        setCountries(newCountries.filter(c => allowedCountries.includes(c)));
    }, [setRegions, regions, allRegions, allowedCountries, setCountries]);

    const onRegionRemove = useCallback((index: number) => {
        if (regions && allRegions && allowedCountries) {
            const newRegions = [...regions];
            if (newRegions[index]) {
                newRegions.splice(index, 1)
            }
            setRegions(newRegions);
            const regionCountries = allRegions.data.filter(i => newRegions.includes(i.id)).map(r => r.countries);
            let newCountries = [];
            for (let i = 0; i < regionCountries.length; i++) {
                // @ts-expect-error can be undefined
                newCountries = newCountries.concat(regionCountries[i]);
            }
            setCountries(newCountries.filter(c => allowedCountries.includes(c)));
        }
    }, [regions, allRegions, allowedCountries, setRegions, setCountries]);

    const onSelectAllCountries = useCallback(() => {
        if (!allCountries || !allowedCountries)
            return;
        setCountries(allowedCountries);
    }, [allCountries, allowedCountries, setCountries]);

    const onProviderCredentialsOpen = useCallback((p: BrokerProviderPaymentMethod) => {
        if (p.methods && p.methods.length > 0 && p.methods[0]) {
            openCredentialsModal(p.methods[0], true);
        }
    }, [openCredentialsModal]);

    const onCurrencyChange = useCallback((key: string, value: string | number) => {
        if (!cashiers)
            return;
        const currency = typeof value === "string" ? parseInt(value) : value;
        let index = 0;
        cashiers.find((item, idx) => {
            index = idx;
            return item.currency === currency;
        })
        console.log(key);
        setSelectedCurrency(index);
    }, [setSelectedCurrency, cashiers]);

    const onCashierFieldChanged = useCallback((index: number, field: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
        if (e?.currentTarget) {
            const type = field === "info" ? "string" : "number";
            const val = typeof e.currentTarget.value === type ? e.currentTarget.value : parseFloat(e.currentTarget.value as string);

            if (cashiers && cashiers[index]) {
                const newCashier = [...cashiers];
                // @ts-expect-error can be undefined
                newCashier[index] = {...newCashier[index], [field as keyof typeof newCashier[index]]: val};
                setCashiers(newCashier);
            }
        }
    }, [cashiers]);

    const onSortingOrderChanged = (order: BrokerPaymentMethod[]) => {
        API.setSortingOrder({
            order: order.map((item) => {
                return {
                    id: item.enabled_id,
                    order: item.order
                };
            })
        }).then(() => {
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.saved')}</>, "bg-teal-500");
            // setSortingMethods(order);
        });
    }

    const filters = useMemo(() => {
        if (!system || !system.system?.providers)
            return <></>

        return <>
            {!isSortingMode &&
                <FilterDropdown list={system.system.providers} name={t('admin.broker_payment_methods.provider')}
                                selected={provider}
                                onChange={onProviderChanged} className="mb-2 lg:mb-0"/>}
            {user && user.user.permissions.includes('modify sort order') ? <Button color={"black"}
                                                                                   className="px-4 py-3 text-white bg-black w-full lg:w-[auto!important] lg:float-right"
                                                                                   onClick={() => setIsSortingMode(!isSortingMode)}>{t('admin.broker_payment_methods.sort_payment_methods')}</Button>
                : ''
            }
        </>
    }, [system, provider, onProviderChanged, isSortingMode, t, user]);

    const credentialsModal = useMemo(() => {
        if (!modalPaymentMethod || !modals.credentials.isComponentVisible || !inputs)
            return <></>

        return <Modal refer={modals.credentials.ref} showModal={modals.credentials.isComponentVisible}
                      title={modalPaymentMethod.name + ' credentials'}>
            <div className="max-w-md w-full">
                <div className="grid grid-cols-2 modal-grid-inputs gap-2">
                    <GenericInputs inputs={inputs.currencyInputs} setLocalValue={setLocalValue} values={values}/>
                </div>
                <GenericInputs inputs={inputs.singleInputs} setLocalValue={setLocalValue} values={values}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={inProgress}
                            onClick={() => saveCredentials()}>{inProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => closeCredentialsModal()}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modalPaymentMethod, modals.credentials, inputs, values, saveCredentials]);

    const countriesModal = useMemo(() => {
        if (!modalPaymentMethod || !modals.countries.isComponentVisible || !countries || !allCountries || !allowedCountries || !allRegions || !regions)
            return <></>

        return <Modal refer={modals.countries.ref} showModal={modals.countries.isComponentVisible}
                      className="min-w-[35vw]"
                      title={modalPaymentMethod.name + ' countries'}>
            <div className="w-full">
                <DropdownInput id="regions_form" name={t('admin.broker_payment_methods.regions')}
                               className="mb-4"
                               searchable={true}
                               multiple={true}
                               withLabel={false}
                               selected={regions}
                               onChange={changeRegions}
                               onRemove={onRegionRemove}
                               list={allRegions?.data ?? []}/>

                <DropdownInput id="countries_form" name={t('admin.broker_payment_methods.countries')}
                               searchable={true}
                               multiple={true}
                               withLabel={false}
                               withAllButton={true}
                               selected={countries}
                               onChange={changeCountries}
                               onRemove={onCountryRemove}
                               onSelectAll={onSelectAllCountries}
                               list={allCountries.data.filter(i => allowedCountries.includes(i.id)).map((item) => {
                                   return {
                                       id: item.id,
                                       name: item.name,
                                       icon: <CountryAvatar country={item} withName={false}
                                                            className="h-3 max-w-[1rem]"/>
                                   }
                               }) ?? []}/>

                <h3 className="font-bold text-md my-8">{t('admin.broker_payment_methods.countries')}</h3>
                <div className="grid grid-cols-3 gap-x-2">
                    {allCountries.data.filter(c => countries.includes(c.id)).map((country, index) => <RemovableCountry
                        key={country.id} country={country} index={index} onCountryRemove={onCountryRemove}/>)}
                </div>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={inProgress}
                            onClick={() => saveCountries()}>{inProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => closeCountriesModal()}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modalPaymentMethod, allowedCountries, countries, allowedCountries, regions, allRegions, modals.countries, closeCountriesModal, saveCountries]);

    const cashiersModal = useMemo(() => {
        if (!modalPaymentMethod || !modals.cashier.isComponentVisible || !system || !system.system || !system.system.currencies || !cashiers)
            return <></>

        return <Modal refer={modals.cashier.ref} showModal={modals.cashier.isComponentVisible}
                      className="min-w-[35vw]"
                      title={modalPaymentMethod.name + ' cashier'}>
            <div className="w-full max-w-md">
                <MaterialSelect onChange={onCurrencyChange}
                                key={'currency'}
                                options={cashiers.map((item) => {
                                    return {
                                        id: item.currency,
                                        name: system.system?.currencies.find(i => i.id === item.currency)?.code ?? ''
                                    }
                                }) ?? []}
                                required={true}
                                name={'currency'}
                                placeholder={t('admin.broker_payment_methods.currency')}/>

                {cashiers[selectedCurrency] ?
                    <>
                        <div className="mt-4">
                            <OutlineInput onChange={(e) => onCashierFieldChanged(selectedCurrency, 'min', e)}
                                          value={cashiers[selectedCurrency]?.min}
                                          name={t('admin.broker_payment_methods.min')} type="number"
                                          validated={false}
                                          fieldName="min"/>
                        </div>
                        <div className="mt-4">
                            <OutlineInput onChange={(e) => onCashierFieldChanged(selectedCurrency, 'max', e)}
                                          value={cashiers[selectedCurrency]?.max}
                                          name={t('admin.broker_payment_methods.max')} type="number"
                                          validated={false}
                                          fieldName="max"/>
                        </div>
                        <div className="mt-4">
                            <OutlineTextarea validated={false} fieldName="info"
                                             name={t('admin.broker_payment_methods.info')}
                                             onChange={(e) => onCashierFieldChanged(selectedCurrency, 'info', e)}
                                             value={cashiers[selectedCurrency]?.info ?? ''}/>
                        </div>
                    </> : ''}

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={inProgress}
                            onClick={() => saveCashiers()}>{inProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => closeCashiersModal()}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modalPaymentMethod, modals.cashier, selectedCurrency, closeCashiersModal, saveCountries]);

    const banksModal = useMemo(() => {
        if (!modalPaymentMethod || !modals.banks.isComponentVisible || !banks.length || !user)
            return <></>

        return <Modal refer={modals.banks.ref} showModal={modals.banks.isComponentVisible}
                      className="min-w-[35vw]"
                      title={modalPaymentMethod.name + ' banks'}>
            <div className="w-full">
                <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 md:gap-2">
                    {banks.map((item, i) => <div key={`bank_${i}`}>
                        <Checkbox checked={item.enabled}
                                  onChange={() => onBankStatusChanged(i, !item.enabled)}
                                  color={'teal'} crossOrigin={''}
                                  label={item.name}
                                  value={item.id}/>
                    </div>)}
                </div>
                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={inProgress}
                            onClick={() => saveBanks()}>{inProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => closeBanksModal()}>{t('close')}</span>
                </div>
            </div>

        </Modal>
    }, [modalPaymentMethod, modals.banks, selectedCurrency, closeBanksModal, saveBanks]);


    const brokersUI = useMemo(() => {
        if (!paymentMethods || !user || !user.user || isSortingMode)
            return <></>;

        const list = provider ? paymentMethods.filter(p => p.provider.id === provider) : paymentMethods;

        return <div className="mt-4">{list.map((p, pIndex) => <div key={p.provider.name}
                                                                   className={`${p.provider.id === openProvider ? 'border border-blue-gray-200 dark:border-blue-gray-500' : ''} rounded-md mb-2`}>
            <div
                className={`text-gray-700 dark:text-gray-300 p-6 ${p.provider.id === openProvider ? '' : 'hover:bg-gray-300 dark:hover:text-black hover:text-black rounded-md border border-blue-gray-200 dark:border-blue-gray-500 cursor-pointer'}`}
                onClick={() => onOpenProvider(p.provider.id)}>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <ProviderAvatar image={`${window.location.origin}/images/psps/${p.provider.image}`}/>
                        <span>{p.provider.name} </span>
                        <span
                            className="inline-block ml-4 font-bold text-gray-500 ">[{p.provider.enabled_count}/{p.methods.length}]</span>
                    </div>
                    <div className="text-right">
                        {p.methods.filter(i => i.status && i.type !== 'Wire Transfer').length && user.user.permissions.includes('update credentials') ? (
                            <Button className="text-sm bg-black text-white px-2 py-1"
                                    onClick={() => onProviderCredentialsOpen(p)} disabled={inProgress}>
                                {inProgress ?
                                    <Loader/> : <FontAwesomeIcon icon={solid('key')} className="w-5"/>}</Button>) : ''}
                    </div>
                </div>
            </div>
            <Collapse open={p.provider.id === openProvider}>
                <div className="px-6 py-0">
                    <table className="w-full table-auto text-left mb-4">
                        <thead>
                        <tr>
                            <th className="p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">#</th>
                            <th className="p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{t('admin.broker_payment_methods.table.name')}</th>
                            <th className="p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{t('admin.broker_payment_methods.table.enable')}</th>
                            <th className="p-4 border-b border-blue-gray-50 dark:border-blue-gray-500 text-right">{t('admin.broker_payment_methods.table.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {p.methods.map((method, index) =>
                            <tr key={method.name}>
                                <td className="w-[5%] p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{index + 1}</td>
                                <td className="w-[40%] p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">
                                    <ProviderAvatar
                                        image={`${window.location.origin}/images/psps/${method.image}`}/>
                                    <span>{method.name} </span>
                                </td>
                                <td className="p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">
                                    <Checkbox checked={method.status}
                                              onChange={() => onEnableChanged(pIndex, index, !method.status)}
                                              color={'teal'} crossOrigin={''}
                                              disabled={inProgress || !user.user.permissions.includes('switch status')}
                                              value={1}/>
                                </td>
                                <td className="w-[40%] text-right py-4 border-b border-blue-gray-50 dark:border-blue-gray-500">
                                    {
                                        method.status ? <>
                                            {method.type !== 'Wire Transfer' && user.user.permissions.includes('update cashier') && method.has_banks ?
                                                <Button
                                                    disabled={inProgress}
                                                    onClick={() => openBanksModal(method)}
                                                    className="text-sm shadow-none hover:shadow-none bg-warning text-white px-1 py-1 mr-1">{inProgress ?
                                                    <Loader/> : <FontAwesomeIcon
                                                        icon={solid('building-columns')} className="w-5"/>}</Button>
                                                : ''}
                                            {user.user.permissions.includes('update countries') ? <Button
                                                disabled={inProgress}
                                                onClick={() => openCountriesModal(method)}
                                                className="text-sm shadow-none hover:shadow-none bg-info text-white px-1 py-1 mr-1">{inProgress ?
                                                <Loader/> : <FontAwesomeIcon
                                                    icon={solid('globe')} className="w-5"/>}</Button> : ''}
                                            {method.type !== 'Wire Transfer' && user.user.permissions.includes('update cashier') ?
                                                <Button
                                                    disabled={inProgress}
                                                    onClick={() => openCashiersModal(method)}
                                                    className="text-sm shadow-none hover:shadow-none bg-info text-white px-1 py-1 mr-1">{inProgress ?
                                                    <Loader/> : <FontAwesomeIcon
                                                        icon={solid('cash-register')} className="w-5"/>}</Button>
                                                : ''}
                                            {method.type !== 'Wire Transfer' && user.user.permissions.includes('update credentials') ?
                                                <Button
                                                    disabled={inProgress}
                                                    onClick={() => openCredentialsModal(method)}
                                                    className="text-sm shadow-none hover:shadow-none bg-black px-1 py-1"> {inProgress ?
                                                    <Loader/> : <FontAwesomeIcon
                                                        icon={solid('key')} className="w-5"/>}</Button> : ''}
                                        </> : ''
                                    }
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </Collapse>
        </div>)}</div>;
    }, [broker, user, isSortingMode, paymentMethods, provider, onEnableChanged, openCredentialsModal, inProgress, openProvider, onOpenProvider, onProviderCredentialsOpen, onEnableChanged]);

    const sortingUI = useMemo(() => {
        if (!user || !user.user || !isSortingMode || !sortingMethods)
            return <></>;

        return <DraggableTable
            table={sortingMethods}
            onChange={onSortingOrderChanged}
        />
    }, [user, isSortingMode, sortingMethods, onSortingOrderChanged]);

    useEffect(() => {
        setTitle(t('admin.broker_payment_methods.update.title', {name: broker ? `${broker.first_name} ${broker.last_name}` : ''}));
        dispatch(setSidebarTitle(t('admin.broker_payment_methods.update.title', {name: broker ? `${broker.first_name} ${broker.last_name}` : ''})));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendUserRequest();
                sendRequest();
            }).catch();
        }
        dispatch(clear())
    }, [user, dispatch, setSidebarTitle]);

    return (
        <PageBlock>
            <Card className="page-card-block mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {!isForbidden && <>{filters}
                        {credentialsModal}
                        {countriesModal}
                        {cashiersModal}
                        {banksModal}
                        {brokersUI}
                        {sortingUI}
                    </>
                    }
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(BrokerPaymentMethods);
